import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {Alert, Button, styled} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ContentSubmission} from '@/types/contentSubmission';
import styles from './ContentSubmissionOutdatedBanner.module.scss';

export interface ContentSubmissionOutdatedBannerProps {
  testID?: string;
  contentSubmission: ContentSubmission;
  activeVersionId: string | null;
  onVersionChange: (versionId: string) => void;
}

const StyledAlert = styled(Alert)(() => ({
  'padding': '6px 16px',
  'borderRadius': 0,
  'border': 'none',
  'backgroundColor': designSystemToken('semantic.bg.warning-secondary'),
  'color': designSystemToken('semantic.fg.warning'),
  '& .MuiAlert-icon': {
    marginRight: '12px',
    padding: '7px 0',
    color: designSystemToken('semantic.fg.warning'),
  },
  '& .MuiAlert-message': {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '0',
    color: designSystemToken('semantic.fg.warning'),
  },
  '& .MuiAlert-action': {
    padding: '0 0 0 16px',
    alignItems: 'center',
  },
}));

const StyledButton = styled(Button)(() => ({
  'color': designSystemToken('semantic.fg.warning'),
  'textTransform': 'capitalize',
  'padding': '4px 5px',
  'minWidth': 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

function ContentSubmissionOutdatedBanner({
  testID = 'content-submission-outdated-banner',
  contentSubmission,
  activeVersionId,
  onVersionChange,
}: ContentSubmissionOutdatedBannerProps) {
  const {versions, latestVersionId} = contentSubmission;

  if (
    !activeVersionId ||
    !latestVersionId ||
    activeVersionId === latestVersionId ||
    !versions
  ) {
    return null;
  }

  // API returns versions newest-first, so version number = length - index
  const activeVersionNumber =
    versions.length - versions.findIndex((v) => v.id === activeVersionId);
  const latestVersionNumber =
    versions.length - versions.findIndex((v) => v.id === latestVersionId);

  return (
    <StyledAlert
      data-testid={testID}
      severity="warning"
      variant="standard"
      className={styles.alert}
      action={
        <StyledButton
          endIcon={<ChevronRightIcon className={styles.chevron} />}
          onClick={() => onVersionChange(latestVersionId)}
        >
          {translate('switch-to-version', {n: latestVersionNumber})}
        </StyledButton>
      }
    >
      <Body size="sm" color={designSystemToken('semantic.fg.warning')}>
        {translate('viewing-outdated-version', {
          active: activeVersionNumber,
          latest: latestVersionNumber,
        })}
      </Body>
    </StyledAlert>
  );
}

export default ContentSubmissionOutdatedBanner;
